import React from 'react';
import { Link } from 'react-router-dom';
import PortalMark from '../PortalMark';
import { ringBorder, C } from './tokens';
import './kit.css';

export * from './tokens';

/**
 * Pill button — handoff §Pill button variants.
 *   variant: primary | secondary | outline | text
 *   on: 'black' | 'white' (the surface it sits on)
 *   size: pixel height (52 auth, 48 CTA, 40 profile, 38 rows, 34 chips, 28 copy)
 */
export function Pill({ variant = 'primary', on = 'black', size = 40, fontSize, tracking, weight, padding, className = '', style, as: As = 'button', type, ...rest }) {
  const fs = fontSize ?? (size >= 48 ? 12 : size >= 34 ? 11 : 10);
  const ls = tracking ?? (variant === 'primary' && size >= 48 ? '.18em' : variant === 'primary' ? '.16em' : '.14em');
  const fw = weight ?? (variant === 'primary' ? 800 : 700);
  const pad = padding ?? (size >= 48 ? '0 26px' : size >= 38 ? '0 16px' : '0 12px');
  const props = As === 'button' ? { type: type || 'button' } : {};
  return (
    <As
      {...props}
      className={`pk pk-btn pk-${variant}-on-${on} ${className}`}
      style={{ height: size, padding: pad, fontSize: fs, letterSpacing: ls, fontWeight: fw, textTransform: 'uppercase', ...style }}
      {...rest}
    />
  );
}

/** ← BACK pill: 40px, #161616, 11px/700 .14em, a 14px arrow with square caps. */
export function BackPill({ to, onClick, children = 'BACK', on = 'black', style, className = '' }) {
  const inner = (
    <>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
      {children}
    </>
  );
  const base = { display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px 0 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textDecoration: 'none', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0, ...style };
  const cls = `pk pk-btn pk-secondary-on-${on} ${className}`;
  if (to) return <Link to={to} onClick={onClick} className={cls} style={base}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls} style={base}>{inner}</button>;
}

/** The animated ring circle with a check — the done block, the published hero. */
export function RingCircle({ size = 56, border = 2, check = 22, stroke = 2.5, fill = '#000', className = '', style }) {
  return (
    <span
      data-motion="1"
      className={`pk-ring-anim ${className}`}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, border: `${border}px solid transparent`, borderRadius: 999, boxSizing: 'border-box', background: ringBorder(fill), flexShrink: 0, ...style }}
    >
      <svg width={check} height={check} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={stroke} strokeLinecap="square" aria-hidden="true"><path d="M4 12l5 5L20 6" /></svg>
    </span>
  );
}

/** Segmented pill: `tabs` = [{ key, label }], `value`, `onChange`. Ring border on the active tab when `ring`. */
export function Segmented({ tabs, value, onChange, on = 'black', height = 40, fontSize = 11, tracking = '.16em', ring = on === 'black', grow = true, style, label }) {
  const track = on === 'black' ? '#000' : '#F2F2F2';
  return (
    <div role="tablist" aria-label={label} style={{ display: grow ? 'grid' : 'flex', gridTemplateColumns: grow ? `repeat(${tabs.length}, minmax(0,1fr))` : undefined, gap: 2, padding: 3, borderRadius: 999, background: track, ...style }}>
      {tabs.map((t) => {
        const active = t.key === value;
        const bg = active ? (ring ? ringBorder('#161616') : '#000') : 'transparent';
        const fg = active ? '#fff' : on === 'black' ? '#8A8A8A' : '#000';
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className="pk"
            style={{ height, padding: grow ? 0 : '0 16px', border: '2px solid transparent', borderRadius: 999, background: bg, color: fg, fontSize, fontWeight: 700, letterSpacing: tracking, cursor: 'pointer', boxSizing: 'border-box', whiteSpace: 'nowrap', textTransform: 'uppercase', transition: 'color .2s, background-color .2s' }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/** The 32px spinning portal for auth / planner headers. The brand's mark, never recoloured. */
export function SpinningO({ size = 32 }) {
  return <PortalMark size={size} />;
}

/** A small status dot. */
export function Dot({ color = C.pink, size = 8, pulse = false, glow = false, style }) {
  return <span data-motion="1" className={pulse ? 'pk-pulse' : undefined} aria-hidden="true" style={{ display: 'inline-block', flexShrink: 0, width: size, height: size, borderRadius: '50%', background: color, boxShadow: glow ? `0 0 10px ${color}` : undefined, ...style }} />;
}
